import React, { useState } from 'react'

import {
  Stack,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material'
import {
  WarningAmberOutlined,
  ElectricalServicesOutlined,
  MoreTimeOutlined,
  ScheduleOutlined,
} from '@mui/icons-material'

import { IdleTariffSchedule } from '../../IdleTariffSchedule'

import { numericUtils } from '@/utils/numeric'
import { tariffUtils } from '@/utils/tariff'

import { ChargingStepProps, StartChargingStep } from '../hooks/useStartCharging/types'
import { ChargingStepActions } from '../components/ChargingStepActions'
import { timeUtils } from '@/utils/time'

type IdleRateConfirmationStepProps = ChargingStepProps

export const IdleRateConfirmationStep: React.FC<IdleRateConfirmationStepProps> = ({ startChargingMethods }) => {
  const { chargingPoint, chargingConfig, goToStep, isUsingWalletBalanceOnly } = startChargingMethods
  const [confirmed, setConfirmed] = useState(false)

  const idleRate = chargingPoint?.current_idle_rate ?? chargingPoint?.idle_rate ?? 0
  const idleGracePeriod = chargingPoint?.idle_grace_period_minutes ?? chargingConfig?.idle_grace_period_minutes
  const formattedIdleGrace = timeUtils.formatMinutesToTime(idleGracePeriod) || 'N/A'
  const idleTimeline = chargingPoint?.idle_timeline
  const hasIdleSchedule = tariffUtils.hasVaryingRates(idleTimeline)

  const handleConfirm = () => {
    goToStep(StartChargingStep.CONFIRMATION)
  }

  const handleGoBack = () => {
    goToStep(isUsingWalletBalanceOnly ? StartChargingStep.AMOUNT : StartChargingStep.PAYMENT_METHOD)
  }

  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningAmberOutlined color="warning" />
          <Typography variant="h6" fontWeight="bold">
            Aviso de ociosidade
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {Number(idleGracePeriod) > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <ElectricalServicesOutlined sx={{ fontSize: 20, marginTop: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Quando a recarga terminar, o carregador ficará reservado para você por{' '}
                <Typography component="span" variant="body2" fontWeight="bold" color="primary">
                  {formattedIdleGrace}
                </Typography>
                .
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <MoreTimeOutlined sx={{ fontSize: 20, marginTop: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {hasIdleSchedule ? 'Taxa vigente agora: ' : 'Após a tolerância, será cobrada uma taxa de '}
              <Typography component="span" variant="body2" fontWeight="bold" color="primary">
                {numericUtils.formatNumberToBRLCurrency(idleRate)}/min
              </Typography>{' '}
              {hasIdleSchedule
                ? 'A cobrança após a tolerância segue as faixas abaixo até retirar o veículo.'
                : 'até o veículo ser retirado.'}
            </Typography>
          </Box>

          {hasIdleSchedule && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <ScheduleOutlined sx={{ fontSize: 20, marginTop: 0.5, color: 'text.secondary' }} />
              {chargingPoint && <IdleTariffSchedule chargingPoint={chargingPoint} />}
            </Box>
          )}

          <FormControlLabel
            control={<Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />}
            label={
              <Typography variant="body2" color="text.secondary">
                Entendi e concordo com a cobrança da taxa de ociosidade.
              </Typography>
            }
            sx={{ marginTop: 1 }}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <ChargingStepActions sx={{ width: '100%' }}>
          <ChargingStepActions.ForwardButton onClick={handleConfirm} disabled={!confirmed}>
            Continuar
          </ChargingStepActions.ForwardButton>
          <ChargingStepActions.BackwardButton onClick={handleGoBack} />
        </ChargingStepActions>
      </DialogActions>
    </>
  )
}
